/**
 * vizStore — the ONE shared visualizer store used by every editor (Synth VR,
 * Flow, Console). The visualizer is orthogonal to the editor, so its state lives
 * here, not inside any editor's store. Editors just mount <VisualizerPanel/> and
 * <VisualizerBackground/> (passing only their playhead time + audio analyser).
 *
 * Holds the loaded .visualizer/.vizproject, the library, the on/off + reactivity
 * + camera settings, and handles import/activate/remove with IndexedDB
 * persistence (so a loaded visual + library survive refreshes and are shared
 * across all editors).
 */

import { create } from 'zustand';
import { parseVizFile, type ParsedVizProject } from './vizproject';
import { idbGet, idbSet, idbDel } from './vizStorage';

export interface VizLibItem { id: string; name: string; kind: 'scene' | 'project' }
export type VizReactTo = 'audio' | 'notes';
export type VizCameraMode = 'auto' | 'manual';

const LIB_KEY = 'viz:lib:index';
const ACTIVE_KEY = 'viz:lib:active';
const rawKey = (id: string) => `viz:lib:raw:${id}`;
// Light prefs live in localStorage (small, synchronous on boot).
const PREFS_KEY = 'viz:prefs';

// Min zoom lowered 0.2 → 0.1 so the manual camera can pull 50% further back
// (lower zoom = further out); stays above the 0.05 floor in Scene.setManualCamera.
export const VIZ_ZOOM_MIN = 0.1;
export const VIZ_ZOOM_MAX = 3;

interface VizPrefs {
  enabled: boolean;
  reactTo: VizReactTo;
  cameraMode: VizCameraMode;
  camX: number;
  camY: number;
  camZ: number;
  /** Per-editor, per-visual camera positions. Key: `"${editor}:${visualId}"`. */
  camPositions: Record<string, { x: number; y: number; z: number }>;
}

const DEFAULT_PREFS: VizPrefs = {
  enabled: false, reactTo: 'audio', cameraMode: 'auto', camX: 0, camY: 0, camZ: 1,
  camPositions: {},
};

function loadPrefs(): VizPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_PREFS };
}

interface VizStore extends VizPrefs {
  project: ParsedVizProject | null;
  library: VizLibItem[];
  activeId: string | null;
  restored: boolean;

  setEnabled: (v: boolean) => void;
  setReactTo: (v: VizReactTo) => void;
  setCameraMode: (v: VizCameraMode) => void;
  /** Update camera for the active visual in the given editor. */
  setCam: (p: Partial<{ x: number; y: number; z: number }>, editor: string) => void;
  /** Load saved camera values for the given editor + active visual. */
  syncCam: (editor: string) => void;

  /** Import a .visualizer/.vizproject File: add to the library, make it active. */
  importFile: (file: File) => void;
  /** Activate a library item (re-parses its raw JSON to rebuild media URLs). */
  activate: (id: string) => Promise<void>;
  /** Remove a library item; activates the next one (or clears) if it was active. */
  remove: (id: string) => void;
  /** Load library + active visual from IndexedDB (call once on first mount). */
  restore: () => Promise<void>;
}

function persistPrefs(s: VizStore): void {
  const p: VizPrefs = {
    enabled: s.enabled, reactTo: s.reactTo, cameraMode: s.cameraMode,
    camX: s.camX, camY: s.camY, camZ: s.camZ,
    camPositions: s.camPositions,
  };
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* quota */ }
}

export const useVizStore = create<VizStore>((set, get) => ({
  ...loadPrefs(),
  project: null,
  library: [],
  activeId: null,
  restored: false,

  setEnabled: (v) => { set({ enabled: v }); persistPrefs(get()); },
  setReactTo: (v) => { set({ reactTo: v }); persistPrefs(get()); },
  setCameraMode: (v) => { set({ cameraMode: v }); persistPrefs(get()); },
  setCam: (p, editor) => {
    const s = get();
    const x = p.x ?? s.camX;
    const y = p.y ?? s.camY;
    const z = p.z ?? s.camZ;
    const camPositions = { ...s.camPositions };
    if (s.activeId) camPositions[`${editor}:${s.activeId}`] = { x, y, z };
    set({ camX: x, camY: y, camZ: z, camPositions });
    persistPrefs(get());
  },
  syncCam: (editor) => {
    const s = get();
    const pos = s.activeId ? s.camPositions[`${editor}:${s.activeId}`] : undefined;
    set({ camX: pos?.x ?? 0, camY: pos?.y ?? 0, camZ: pos?.z ?? 1 });
  },

  importFile: (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      void (async () => {
        try {
          const text = ev.target?.result as string;
          const raw = JSON.parse(text);
          const kind: 'scene' | 'project' | null =
            raw?.format === 'visualizer-project' && raw.project?.timeline?.sceneTrack?.clips ? 'project'
            : raw?.format === 'visualizer-scene' && Array.isArray(raw.scenes) && raw.scenes.length > 0 ? 'scene'
            : null;
          if (!kind) { alert('Not a valid .visualizer or .vizproject file.'); return; }
          const parsed = parseVizFile(raw);
          if (!parsed) { alert('Could not parse the visualizer.'); return; }
          const id = `viz-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
          const item: VizLibItem = { id, name: file.name || 'Visual', kind };
          const nextLib = [...get().library, item];
          const prev = get().project;
          if (prev && prev !== parsed) prev.dispose?.();
          set({ library: nextLib, project: parsed, activeId: id, enabled: true });
          persistPrefs(get());
          await idbSet(rawKey(id), text);
          await idbSet(LIB_KEY, nextLib);
          await idbSet(ACTIVE_KEY, id);
        } catch { alert('Could not parse the file.'); }
      })();
    };
    reader.readAsText(file);
  },

  activate: async (id) => {
    const text = await idbGet<string>(rawKey(id));
    if (!text) return;
    const parsed = parseVizFile(text);
    if (!parsed) return;
    const prev = get().project;
    if (prev && prev !== parsed) prev.dispose?.();
    set({ project: parsed, activeId: id, enabled: true });
    persistPrefs(get());
    void idbSet(ACTIVE_KEY, id);
  },

  remove: (id) => {
    const nextLib = get().library.filter(v => v.id !== id);
    set({ library: nextLib });
    void idbSet(LIB_KEY, nextLib);
    void idbDel(rawKey(id));
    if (get().activeId === id) {
      if (nextLib.length) { void get().activate(nextLib[0].id); }
      else {
        const prev = get().project;
        prev?.dispose?.();
        set({ project: null, activeId: null, enabled: false });
        persistPrefs(get());
        void idbDel(ACTIVE_KEY);
      }
    }
  },

  restore: async () => {
    if (get().restored) return;
    set({ restored: true });
    try {
      const lib = await idbGet<VizLibItem[]>(LIB_KEY);
      if (lib && lib.length) set({ library: lib });
      const activeId = await idbGet<string>(ACTIVE_KEY);
      const id = (activeId && lib?.find(v => v.id === activeId)) ? activeId : lib?.[0]?.id;
      if (!id) return;
      const text = await idbGet<string>(rawKey(id));
      if (!text || get().project) return;
      const parsed = parseVizFile(text);
      if (parsed) set({ project: parsed, activeId: id });
    } catch { /* ignore restore failures */ }
  },
}));

/** Raw stored JSON string of a library item (the exported .visualizer/.vizproject
 *  contents), or null. Used to bundle a visualizer into an exported map file. */
export async function getVizRawJson(id: string): Promise<string | null> {
  return idbGet<string>(rawKey(id));
}
